/**
 * The panel shown in place of the form when the server rejected a submission.
 *
 * The counterpart to FeedbackThankYou, and deliberately its mirror image: same
 * position, same shape, opposite outcome. A failed submit used to surface as a
 * small red banner above a form that still looked submittable, which reads as a
 * validation nitpick rather than "your answers did not reach us."
 *
 * Purely presentational. `onRetry` returns to the form with every answer still
 * in it — the wrapper holds the draft, so nothing is retyped.
 */

type Props = {
  title?: string;
  /** What actually went wrong. Comes from the thrown error, not invented here. */
  message: string;
  retryLabel?: string;
  onRetry: () => void;
};

const AlertIcon = (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const FeedbackError: React.FC<Props> = ({
  title = "That didn't go through",
  message,
  retryLabel = "Try again",
  onRetry,
}) => (
  // Matches FeedbackThankYou's box exactly, so the panel swap animates between
  // two shapes that differ only in content.
  <div
    className="mx-auto flex w-full max-w-2xl flex-col items-center px-6 pb-8 pt-10 text-center"
    role="alert"
  >
    <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600 [.dark_&]:bg-red-950 [.dark_&]:text-red-300">
      {AlertIcon}
    </div>

    <h2 className="text-xl font-bold text-gray-900 [.dark_&]:text-zinc-100 mb-2">
      {title}
    </h2>

    <p className="text-sm text-gray-600 [.dark_&]:text-zinc-400 mb-7 max-w-md">
      {message}
    </p>

    <button
      type="button"
      onClick={onRetry}
      autoFocus
      className="bg-royal-blue-500 text-white py-3 text-sm px-8 rounded-md font-medium hover:bg-[#5854c7]"
    >
      {retryLabel}
    </button>
  </div>
);

export default FeedbackError;
